/**
 * @file     ListingActionsContext.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  One owner for every listing action on the slug page. The rail,
 *           the top bar, the phone bar and the More menu all call `run(id,
 *           position)`, so each sheet (showing, offer, documents, alerts) is
 *           mounted once and every click lands the same cta_click event. Also
 *           owns Add to list, Compare and Share state, and the page view event.
 */
'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { analyticsEvents } from '@/core/constants/analytics';
import { propertyCanonicalRoute } from '@/core/constants/routes';
import {
  listingActionsFor,
  type ActionId,
  type CtaPosition,
  type ResolvedListingActions,
} from '@/core/config/listingActions';
import { useCompare } from '@/components/compare/CompareContext';
import { useLeadCapture, type ListingContext } from '@/components/lead-capture/LeadCaptureContext';
import { usePreApproval } from '@/components/lead-capture/PreApprovalContext';
import { useWishlist } from '@/components/properties/useWishlist';
import { WatchedSnackbar } from '@/features/properties/components/PropertyCard/cardChrome';
import { cityIntent, listingLikeThisIntent, neighbourhoodIntent, type ShortlistIntent } from '@/features/seo/shortlistIntent';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useListingEvents } from '@/hooks/useListingEvents';
import type { ListingLeadType } from '@/core/analytics/listingEvents';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { useListingDetailView, useListingSave } from '../../DetailClientChrome';
import { useListingShare } from '../../ShareButton';
import { sellerValuationHref } from '@/features/lead-flows/sellerPrefill';
import type { ListingActionsListing } from './listingActionsListing';
import { clockFromLabel } from '@/utils/showingCalendar';
import { useListingGate } from '../ListingGate';
import { supportsDocumentPackage } from './documentRecommendations';
import { ScheduleShowingModal } from './ScheduleShowingModal';
import { SubmitOfferAttemptModal } from './SubmitOfferAttemptModal';
import { ViewingRequestSheet } from '@/features/rentals-listing/ViewingRequestSheet';

const AlertSignupModal = dynamic(() => import('@/features/seo/AlertSignupModal').then((m) => m.AlertSignupModal));

export interface ListingActionTarget {
  href?: string;
  onClick: (event?: { preventDefault: () => void }) => void;
}

interface ListingActionsValue {
  listing: ListingActionsListing;
  actions: ResolvedListingActions;
  signedIn: boolean;
  target: (id: ActionId, position: CtaPosition) => ListingActionTarget;
  showingRequested: boolean;
  offerAttempted: boolean;
  /** A rental viewing request was sent from this page. */
  viewingRequested: boolean;
  saved: boolean;
  savePending: boolean;
  toggleSave: () => void;
  inCompare: boolean;
  compareFull: boolean;
  toggleCompare: () => void;
  share: () => void;
  /** Opens the showing sheet; with a time it opens on step 2 (open-house tiles). */
  openShowing: (preset?: ShowingPreset | null) => void;
  /** Past the free preview: the gate owns the actions until an account exists. */
  gated: boolean;
}

export interface ShowingPreset {
  date: string;
  /** "HH:MM" Pacific. */
  time?: string | null;
  endTime?: string | null;
}

const Ctx = createContext<ListingActionsValue | null>(null);

export function useListingActions(): ListingActionsValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useListingActions must be called inside <ListingActionsProvider>.');
  return value;
}

/** For surfaces that also render outside the slug page (the photo gallery in quick views). */
export function useOptionalListingActions(): ListingActionsValue | null {
  return useContext(Ctx);
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

interface Props {
  listing: ListingActionsListing;
  signedIn: boolean;
  /** Server-rendered saved state, so Add to list paints correctly. */
  isSaved: boolean;
  /** The listing page, the card quick view or the map sheet: every event carries it. */
  surface?: ListingSurface;
  /** "Value my home" link with the listing's facts prefilled (sellerValuationHref.ts). */
  valuationHref?: string;
  children: ReactNode;
}

/** The surface the lead sheets record (their older two-value field, plus quick_view). */
const SHEET_SURFACE: Record<ListingSurface, 'slug_page' | 'map_sheet' | 'quick_view'> = {
  page: 'slug_page',
  quick_view: 'quick_view',
  map_sheet: 'map_sheet',
};

export function ListingActionsProvider({ listing, signedIn, isSaved, surface = 'page', valuationHref: prefilledValuationHref, children }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const gate = useListingGate();
  const gated = gate?.locked === true;
  const { trackEvent, trackCtaClick } = useAnalytics();
  const sheetSurface = SHEET_SURFACE[surface];
  const quickView = surface !== 'page';
  const { open: openLeadCapture } = useLeadCapture();
  const { open: openPreApproval } = usePreApproval();
  const compare = useCompare();
  const wishlist = useWishlist();
  const isMls = listing.source === 'mls';
  const listingKey = listing.listingKey ?? listing.slug;
  const pagePath = propertyCanonicalRoute(listing.slug);
  const track = useListingEvents();
  // The action that opened the sheet, so lead_submitted carries its cta_id.
  const lastCtaRef = useRef<ActionId | undefined>(undefined);
  // Rentals add listing_type and listing_source to every listing event; a sale page keeps its exact payloads.
  const kind = useMemo(
    () => (listing.listingType === 'lease' ? { listing_type: 'lease' as const, listing_source: 'mls' as const } : {}),
    [listing.listingType],
  );
  const trackLead = useCallback(
    (leadType: ListingLeadType, workingWithRealtor?: boolean) =>
      track('lead_submitted', {
        lead_type: leadType,
        cta_id: lastCtaRef.current,
        surface,
        listing_key: listingKey,
        ...(workingWithRealtor === undefined ? {} : { working_with_realtor: workingWithRealtor }),
        ...kind,
      }),
    [kind, listingKey, surface, track],
  );
  useEffect(() => {
    track('listing_view', { surface, state: listing.state, listing_key: listingKey, ...kind });
    // One view per listing; a state re-resolve on the same page is not a new view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingKey]);

  useListingDetailView({
    listingKey,
    city: listing.city,
    listingSlug: listing.slug,
    listPrice: listing.price,
    enabled: isMls && !quickView,
  });

  const mlsSave = useListingSave({
    listingKey,
    city: listing.city,
    isAuthenticated: signedIn,
    isSaved,
    returnPath: pagePath,
  });
  const { share, toast } = useListingShare({ listingKey, title: listing.title });

  const actions = useMemo(() => {
    if (gated) return listingActionsFor('gated', { signedIn });
    const resolved = listingActionsFor(listing.state, { signedIn });
    // Request documents only where this home has a document package to ask for.
    const hasDocuments = supportsDocumentPackage(listing.documentRecommendations);
    return { ...resolved, menu: resolved.menu.filter((id) => id !== 'request_documents' || hasDocuments) };
  }, [gated, listing.state, listing.documentRecommendations, signedIn]);

  const [showingOpen, setShowingOpen] = useState(false);
  const [showingPreset, setShowingPreset] = useState<ShowingPreset | null>(null);
  const [showingRequested, setShowingRequested] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAttempted, setOfferAttempted] = useState(false);
  const [viewingOpen, setViewingOpen] = useState(false);
  const [viewingRequested, setViewingRequested] = useState(false);
  const [alert, setAlert] = useState<{ intent: ShortlistIntent; place: string } | null>(null);

  const offerListed = actions.menu.includes('make_offer');
  useEffect(() => {
    if (!offerListed || !listing.listingKey) return;
    const ctrl = new AbortController();
    fetch(`/api/offer-attempts?listingKey=${encodeURIComponent(listing.listingKey)}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { attempted?: boolean } | null) => {
        if (!ctrl.signal.aborted && d?.attempted) setOfferAttempted(true);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [offerListed, listing.listingKey]);

  const context: ListingContext = useMemo(
    () => ({
      listingSlug: listing.slug,
      listingSource: listing.source,
      listingTitle: [listing.title, listing.place].filter(Boolean).join(', '),
      listingPrice: listing.price,
      listingMlsNumber: listing.mlsId,
      listingPlace: listing.neighbourhood ?? listing.city,
      listingThumb: listing.thumbUrl,
    }),
    [listing],
  );

  // Sent screens: the similar-homes block on this page, else the area page.
  const seeSimilar = useCallback(() => {
    trackCtaClick('see_similar', pathname || pagePath, listing.areaHref, {
      cta_id: 'see_similar',
      state: listing.state,
      surface,
      position: 'sheet_footer',
      listing_key: listingKey,
    });
    const block = quickView ? null : document.getElementById('similar');
    if (block) block.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    else router.push(listing.areaHref);
  }, [listing.areaHref, listing.state, listingKey, pagePath, pathname, quickView, router, surface, trackCtaClick]);

  const openShowing = useCallback((preset?: ShowingPreset | null) => {
    setShowingPreset(preset ?? null);
    setShowingOpen(true);
  }, []);

  const areaPlace = listing.neighbourhood ?? listing.city ?? 'BC';
  const authHref = useCallback(
    (base: '/sign-in' | '/sign-up') => `${base}?redirect=${encodeURIComponent(pathname || pagePath)}`,
    [pathname, pagePath],
  );
  const valuationHref =
    prefilledValuationHref ?? sellerValuationHref({ street: listing.title, city: listing.city });

  const perform = useCallback(
    (id: ActionId) => {
      switch (id) {
        case 'book_showing':
          openShowing(null);
          trackEvent({
            eventName: analyticsEvents.showingRequestStarted,
            eventType: 'click',
            category: sheetSurface,
            label: 'schedule_showing_modal_opened',
            metadata: { listing_slug: listing.slug },
          });
          return;
        case 'attend_open_house':
          openShowing(
            listing.openHouse
              ? {
                  date: listing.openHouse.date,
                  time: clockFromLabel(listing.openHouse.start),
                  endTime: clockFromLabel(listing.openHouse.end),
                }
              : null,
          );
          trackEvent({
            eventName: analyticsEvents.showingRequestStarted,
            eventType: 'click',
            category: sheetSurface,
            label: 'open_house_attend_opened',
            metadata: { listing_slug: listing.slug },
          });
          return;
        case 'ask_question':
          openLeadCapture({
            mode: 'inquiry',
            listing: context,
            analyticsSurface: sheetSurface,
            onSubmitted: () => trackLead('question'),
            onSeeSimilar: seeSimilar,
          });
          return;
        case 'save_pdf':
          openLeadCapture({
            mode: 'pdf',
            listing: context,
            analyticsSurface: sheetSurface,
            onSubmitted: () => trackLead('pdf'),
            onSeeSimilar: seeSimilar,
          });
          return;
        case 'request_documents': {
          // One sheet: the checklist and the fields. It fires the opened event once.
          const documents = listing.documentRecommendations.map((d) => ({ id: d.id, label: d.label }));
          openLeadCapture({
            mode: 'documents',
            listing: context,
            documentOptions: documents,
            requestedDocuments: documents,
            analyticsSurface: sheetSurface,
            onSubmitted: (workingWithRealtor) => trackLead('documents', workingWithRealtor),
            onSeeSimilar: seeSimilar,
          });
          return;
        }
        case 'make_offer':
          if (!listing.listingKey) return;
          setOfferOpen(true);
          trackEvent({
            eventName: analyticsEvents.propertyOfferAttemptOpened,
            eventType: 'click',
            category: 'property_detail_menu',
            label: 'submit_offer_attempt_cta',
            metadata: { listing_key: listing.listingKey, list_price: listing.price },
          });
          return;
        case 'broker_intro':
          openPreApproval({
            listingKey,
            listingTitle: listing.title,
            listingPrice: listing.price,
            onSubmitted: () => trackLead('broker'),
          });
          return;
        case 'get_listing_alerts':
          setAlert({
            intent: listingLikeThisIntent({
              area: listing.neighbourhood,
              city: listing.city,
              propertyType: listing.propertyTypeSlug,
              noun: listing.propertyNoun,
            }),
            place: areaPlace,
          });
          return;
        case 'request_viewing':
          if (listing.rental) setViewingOpen(true);
          return;
        case 'get_area_alerts':
          setAlert({
            intent: listing.neighbourhood
              ? neighbourhoodIntent(listing.neighbourhood, listing.city)
              : cityIntent(listing.city ?? 'BC'),
            place: areaPlace,
          });
          return;
        default:
          return;
      }
    },
    [areaPlace, context, listing, listingKey, openLeadCapture, openPreApproval, openShowing, seeSimilar, sheetSurface, trackEvent, trackLead],
  );

  const target = useCallback(
    (id: ActionId, position: CtaPosition): ListingActionTarget => {
      const href =
        id === 'value_my_home' ? valuationHref
        : id === 'see_similar' ? listing.areaHref
        : id === 'rental_alerts' ? listing.rental?.alertsHref ?? listing.areaHref
        : id === 'create_account' ? authHref('/sign-up')
        : id === 'sign_in' ? authHref('/sign-in')
        : undefined;
      return {
        href,
        onClick: (event) => {
          lastCtaRef.current = id;
          trackCtaClick(id, pathname || pagePath, href, {
            cta_id: id,
            state: listing.state,
            surface,
            position,
            listing_key: listingKey,
            ...kind,
          });
          if (id === 'create_account' && gate) {
            // Past the free preview the gate itself is the sign-up.
            event?.preventDefault();
            gate.reopen();
            return;
          }
          if (id === 'see_similar') {
            // The similar-homes block on this page is the better destination
            // than leaving it; the area page is the fallback when it is absent.
            const block = quickView ? null : document.getElementById('similar');
            if (block) {
              event?.preventDefault();
              block.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
            }
            return;
          }
          if (!href) perform(id);
        },
      };
    },
    [authHref, gate, kind, listing.areaHref, listing.rental, listing.state, listingKey, pagePath, pathname, perform, quickView, surface, trackCtaClick, valuationHref],
  );

  const wishRef = { source: 'curated' as const, slug: listing.slug };
  const curatedSaved = !isMls && wishlist.has(wishRef);
  const currentlySaved = isMls ? mlsSave.saved : curatedSaved;
  const toggleSave = useCallback(() => {
    track('listing_save', { surface, listing_key: listingKey, saved: !currentlySaved });
    if (isMls) {
      mlsSave.onSave();
      return;
    }
    if (wishlist.has(wishRef)) wishlist.remove(wishRef);
    else {
      wishlist.add(wishRef);
      trackCtaClick('Like Home', 'property_detail', pagePath);
    }
    // wishRef is rebuilt each render from listing.slug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMls, mlsSave, wishlist, trackCtaClick, pagePath, listing.slug, track, listingKey, currentlySaved, surface]);

  const inCompare = compare.has(listingKey);
  const toggleCompare = useCallback(() => {
    track('listing_compare', { surface, listing_key: listingKey, added: !inCompare });
    compare.toggle({ slug: listingKey, title: listing.title, price: listing.price, thumbSrc: listing.thumbUrl });
  }, [compare, listingKey, listing.title, listing.price, listing.thumbUrl, track, inCompare, surface]);

  const value: ListingActionsValue = {
    listing,
    actions,
    signedIn,
    target,
    showingRequested,
    offerAttempted,
    viewingRequested,
    saved: isMls ? mlsSave.saved : curatedSaved,
    savePending: isMls ? mlsSave.pending : false,
    toggleSave,
    inCompare,
    compareFull: compare.isFull && !inCompare,
    toggleCompare,
    share: () => {
      track('listing_share', { surface, listing_key: listingKey });
      void share();
    },
    openShowing,
    gated,
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {toast}
      {isMls ? (
        <WatchedSnackbar open={mlsSave.signInPromptOpen} onClose={mlsSave.closeSignInPrompt} signInHref={mlsSave.signInHref} />
      ) : null}
      <ScheduleShowingModal
        open={showingOpen}
        onClose={() => setShowingOpen(false)}
        onSubmitted={(workingWithRealtor) => {
          setShowingRequested(true);
          trackLead('showing', workingWithRealtor);
        }}
        listingTitle={context.listingTitle}
        listingSlug={listing.slug}
        listingMlsNumber={listing.mlsId}
        preselectedDate={showingPreset?.date ?? null}
        preselectedTime={showingPreset?.time ?? null}
        preselectedEndTime={showingPreset?.endTime ?? null}
        analyticsSurface={sheetSurface}
        listingPrice={listing.price}
        listingPlace={context.listingPlace}
        listingThumb={listing.thumbUrl}
        onSeeSimilar={seeSimilar}
      />
      {listing.listingKey && offerListed ? (
        <SubmitOfferAttemptModal
          open={offerOpen}
          onClose={() => setOfferOpen(false)}
          onSubmitted={(workingWithRealtor) => {
            setOfferAttempted(true);
            trackLead('offer', workingWithRealtor);
          }}
          listingKey={listing.listingKey}
          listingPrice={listing.price}
          listingAddress={context.listingTitle}
          listingSlug={listing.slug}
          listingPlace={context.listingPlace}
          listingThumb={listing.thumbUrl}
          onSeeSimilar={seeSimilar}
        />
      ) : null}
      {listing.rental ? (
        <ViewingRequestSheet
          open={viewingOpen}
          onClose={() => setViewingOpen(false)}
          listing={{
            title: listing.title,
            price: listing.price,
            place: listing.neighbourhood ?? listing.city,
            thumbUrl: listing.thumbUrl,
            address: listing.title,
            mlsId: listing.mlsId,
            brokerageName: listing.rental.brokerageName,
            availableOn: listing.rental.availableOn,
          }}
          target={{ recipient: listing.rental.recipient, listingKey: listing.rental.listingKey, ownerListingId: listing.rental.ownerListingId }}
          surface={surface}
          analyticsListingKey={listingKey}
          analyticsSource="mls"
          onSubmitted={({ workingWithRealtor }) => {
            setViewingRequested(true);
            trackLead('viewing_request', workingWithRealtor);
          }}
          onSeeSimilar={seeSimilar}
        />
      ) : null}
      {alert ? (
        <AlertSignupModal
          open
          onClose={() => setAlert(null)}
          intent={alert.intent}
          locationTag={`listing_${listing.slug}`}
          place={alert.place}
          onSent={() => trackLead('alerts')}
        />
      ) : null}
    </Ctx.Provider>
  );
}
